<template>
  <b-container fluid class="bg-white lz-controls">
    <div class="row" v-if="listItems.length">
      <div class="col-lg-12">
        <b-table
          caption-top
          responsive
          fixed
          striped
          small
          borderless
          sort-icon-left
          no-local-sorting
          table-class="lizard-activity-list lizard-candesic-table lizard-full-width-table"
          thead-class="label"
          tbody-class="value"
          :busy.sync="loading"
          :items="listItems"
          :fields="listFields"
          :per-page="pager.perPage"
          :current-page="pager.currentPage"
          @sort-changed="sortChanged">
          <template #table-caption>
            <span v-if="loading">
              <b-spinner small></b-spinner>
              Loading...
            </span>
            <code
              style="color: #666 !important"
              v-if="pager.totalRows > pager.perPage && !loading">
              Page {{ formatNumber(pager.currentPage) }}/{{
                formatNumber(pager.totalPages)
              }}
            </code>
            <code style="color: #666 !important" v-if="!loading">
              (Total Results: {{ formatNumber(pager.totalRows) }})
            </code>
          </template>
          <template #cell(owner)="data">
            {{ data.item.owner.firstName }} {{ data.item.owner.lastName }}
          </template>
          <template #cell(role)="data">
            <h6>
              <b-badge variant="primary">
                {{ data.item.role }}
              </b-badge>
            </h6>
          </template>
          <template #cell(lastLogin)="data">
            <span
              v-if="extraInfo"
              v-b-tooltip.hover.top="{ variant: 'dark' }"
              :title="`Last Login IP: ${data.item.lastLoginIP}`">
              {{ formatDate(data.item.lastLogin, 'dd/MM/yyyy HH:mm:ss') }}
            </span>
            <span
              v-else
              v-b-tooltip.hover.top="{ variant: 'dark' }"
              :title="
                data.item.lastLogin
                  ? formatDate(data.item.lastLogin, 'dd/MM/yyyy HH:mm:ss')
                  : 'never'
              "
              >{{
                data.item.lastLogin
                  ? formatDistanceToNow(data.item.lastLogin)
                  : 'never'
              }}</span
            >
          </template>
          <template #cell(lastLoginIP)="data">
            <code style="color: #666 !important">{{
              data.item.lastLoginIP
            }}</code>
          </template>
          <template #cell(totalApprovedExpenseClaimGBP)="data">
            <ul class="expense-currencies">
              <li v-if="data.item.totalApprovedExpenseClaimGBP > 0">
                £{{ formatNumber(data.item.totalApprovedExpenseClaimGBP) }}
              </li>
              <li v-if="data.item.totalApprovedExpenseClaimEUR > 0">
                €{{ formatNumber(data.item.totalApprovedExpenseClaimEUR) }}
              </li>
              <li v-if="data.item.totalApprovedExpenseClaimUSD > 0">
                ${{ formatNumber(data.item.totalApprovedExpenseClaimUSD) }}
              </li>
            </ul>
          </template>
          <template #cell(firstTimesheetSubmittedHourDate)="data">
            {{
              formatDate(
                data.item.firstTimesheetSubmittedHourDate,
                'dd/MM/yyyy'
              )
            }}
          </template>
          <template #cell(lastTimesheetSubmittedHourDate)="data">
            {{
              formatDate(data.item.lastTimesheetSubmittedHourDate, 'dd/MM/yyyy')
            }}
          </template>
          <template #cell(lastStatus)="data">
            <h6>
              <b-badge
                :variant="data.item.lastStatus ? 'success' : 'secondary'">
                {{ data.item.lastStatus ? 'Active' : 'Inactive' }}
              </b-badge>
            </h6>
          </template>
          <template #cell(lastUpdate)="data">
            <span
              v-b-tooltip.hover.top="{ variant: 'dark' }"
              :title="formatDate(data.item.lastUpdate, 'dd/MM/yyyy HH:mm:ss')">
              {{ formatDistanceToNow(data.item.lastUpdate) }}
            </span>
          </template>
          <template #cell(extraInfo)="data">
            <b-button size="sm" variant="link" @click="data.toggleDetails">
              {{ data.detailsShowing ? 'Hide' : 'Show' }}
            </b-button>
          </template>
          <template #row-details="row">
            <b-card>
              <b-row class="mb-2">
                <b-col class="text-left">
                  <pre>{{ row }}</pre>
                </b-col>
              </b-row>
            </b-card>
          </template>
        </b-table>
        <b-pagination
          v-if="pager.totalRows > pager.perPage"
          v-model="pager.currentPage"
          :total-rows="pager.totalRows"
          :per-page="pager.perPage"
          @change="GetList"
          aria-controls="activityTable"
          size="sm"
          align="center"></b-pagination>
      </div>
    </div>
  </b-container>
</template>

<script>
import { format as dateFormat, formatDistanceToNow } from 'date-fns'
// parser = new UAParser("user-agent")

export default {
  name: 'UserStatsList',
  props: ['listItems', 'pager', 'loading', 'extra-info'],
  data() {
    return {
      listFields: this.extraInfo
        ? [
            {
              key: 'owner',
              label: 'Name',
              thClass: 'text-center lizard-stickycol-tr',
              tdClass: 'text-left lizard-stickycol-td',
              stickyColumn: true,
              sortable: true
            },
            {
              key: 'role',
              label: 'Role',
              tdClass: 'text-center'
            },
            { key: 'lastStatus', label: 'Status', tdClass: 'text-center' },
            {
              key: 'lastLogin',
              label: 'Last Login',
              tdClass: 'text-center',
              sortable: true
            },
            // { key: 'lastLoginIP', label: 'Last Login IP', tdClass: 'text-center' },
            {
              key: 'totalLogins',
              label: 'Total Logins',
              tdClass: 'text-center'
            },
            {
              key: 'remainedAnnualLeaveAllowanceCurrentYear',
              label: 'Annual Leave Balance',
              tdClass: 'text-center'
            },
            {
              key: 'remainedPersonalLeaveAllowanceCurrentYear',
              label: 'Personal Leave Balance',
              tdClass: 'text-center'
            },
            {
              key: 'totalApprovedExpenseClaimGBP',
              label: 'Approved Expense Claims',
              tdClass: 'text-center'
            },
            // {
            //   key: 'totalApprovedExpenseClaimEUR',
            //   label: 'Approved Expense Claims (EUR)',
            //   tdClass: 'text-center'
            // },
            // {
            //   key: 'totalApprovedExpenseClaimUSD',
            //   label: 'Approved Expense Claims (USD)',
            //   tdClass: 'text-center'
            // },
            {
              key: 'lastTimesheetSubmittedHourDate',
              label: 'Last Time Entry Date',
              tdClass: 'text-center'
            },
            {
              key: 'totalTimesheetSubmittedHoursCurrentYear',
              label: 'Submitted Hours',
              tdClass: 'text-center'
            },
            // {
            //   key: 'totalTimesheetSubmittedHoursPreviousYear',
            //   label: 'Submitted Hours (Prev. Year)',
            //   tdClass: 'text-center'
            // },
            // {
            //   key: 'firstTimesheetSubmittedHourDate',
            //   label: 'First Time Entry Date',
            //   tdClass: 'text-center'
            // },
            {
              key: 'totalTimesheetCalendarDatesCurrentYear',
              label: 'Calendar Dates',
              tdClass: 'text-center'
            },
            {
              key: 'totalTimesheetActualDatesCurrentYear',
              label: 'Actual Dates',
              tdClass: 'text-center'
            },
            {
              key: 'totalStaffedProjects',
              label: 'Staffed Projects',
              tdClass: 'text-center'
            },
            {
              key: 'totalStaffedDays',
              label: 'Staffed Days',
              tdClass: 'text-center'
            },
            { key: 'lastUpdate', label: 'Last Update', tdClass: 'text-center' }
          ]
        : [
            {
              key: 'owner',
              label: 'Name',
              thClass: 'text-center lizard-stickycol-tr',
              tdClass: 'text-left lizard-stickycol-td',
              stickyColumn: true,
              sortable: true
            },
            {
              key: 'role',
              label: 'Role',
              tdClass: 'text-center'
            },
            { key: 'lastStatus', label: 'Status', tdClass: 'text-center' },
            {
              key: 'lastLogin',
              label: 'Last Login',
              tdClass: 'text-center',
              sortable: true
            },
            {
              key: 'remainedAnnualLeaveAllowanceCurrentYear',
              label: 'Annual Leave Balance',
              tdClass: 'text-center'
            },
            {
              key: 'remainedPersonalLeaveAllowanceCurrentYear',
              label: 'Personal Leave Balance',
              tdClass: 'text-center'
            },
            {
              key: 'totalApprovedExpenseClaimGBP',
              label: 'Approved Expense Claims',
              tdClass: 'text-center'
            },
            {
              key: 'lastTimesheetSubmittedHourDate',
              label: 'Last Time Entry Date',
              tdClass: 'text-center'
            },
            {
              key: 'totalTimesheetSubmittedHoursCurrentYear',
              label: 'Submitted Hours',
              tdClass: 'text-center'
            },
            {
              key: 'totalTimesheetCalendarDatesCurrentYear',
              label: 'Calendar Dates',
              tdClass: 'text-center'
            },
            {
              key: 'totalTimesheetActualDatesCurrentYear',
              label: 'Actual Dates',
              tdClass: 'text-center'
            },
            {
              key: 'totalStaffedProjects',
              label: 'Staffed Projects',
              tdClass: 'text-center'
            },
            {
              key: 'totalStaffedDays',
              label: 'Staffed Days',
              tdClass: 'text-center'
            }
          ]
    }
  },
  async mounted() {
    try {
      this.$root.$on('reset-search-user', () => {
        this.q = null
      })
    } catch (Excpetion) {
      console.log('Exception', Excpetion)
    }
  },
  methods: {
    commitToStore(commitName, value) {
      this.$store.commit(commitName, value)
    },
    GetList(page) {
      this.$root.$emit('refresh-userstats-list', page)
    },
    sortChanged({ sortBy: field, sortDesc: order }) {
      this.commitToStore('setUserStatsSort', {
        field,
        order: order ? 'desc' : 'asc'
      })
    },
    formatDate(date, format) {
      if (!date) {
        return '-'
      }
      return dateFormat(new Date(date), format)
    },
    formatDistanceToNow(date) {
      return formatDistanceToNow(new Date(date), { addSuffix: true })
    },
    formatNumber(value) {
      return value ? parseFloat(value).toLocaleString() : '0.00'
    }
  }
}
</script>

<style>
.lizard-full-width-table {
  min-width: 100% !important;
}
.lizard-stickycol-tr {
  color: #fff !important;
  background-color: #66ccff !important;
}

.lizard-stickycol-td {
  color: #666 !important;
  /* background-image: none !important; */
  background-color: transparent !important;
  backdrop-filter: blur(200px);
  -webkit-backdrop-filter: blur(200px); /* For Safari compatibility */
}

.lizard-user-active-btn {
  margin: 0;
  line-height: 20px !important;
  min-width: 60px !important;
  border-radius: 3px !important;
}
.lizard-user-active-btn:hover {
  cursor: pointer;
  color: #eee !important;
}
.lizard-user-active-btn:active {
  color: #ccc !important;
}
.lizard-user-active-btn-disabled {
  margin: 0;
  line-height: 20px !important;
  min-width: 60px !important;
  border-radius: 3px !important;
  cursor: 'not-allowed';
  opacity: 0.5;
}
.lizard-employeeRole {
  font-size: 9pt !important;
  font-weight: bold;
}
.lizard-activity-list {
  font-size: 12px !important;
  color: #666;
  font-weight: bold !important;
}
.lizard-column-index {
  width: 30px !important;
  text-align: center !important;
}
.lizard-column-active {
  width: 50px !important;
  text-align: center !important;
}
.lizard-column-status {
  width: 80px !important;
  text-align: center !important;
}
.lizard-column-actions {
  text-align: center !important;
}
.lizard-column-actions .manage-rates-btn {
  margin: 0;
  font-size: 8pt !important;
  line-height: 20px !important;
  min-width: 60px !important;
  border-radius: 3px !important;
}
.lizard-column-hrate {
  width: 85px !important;
}
.lizard-form-empty-col {
  min-height: 20px !important;
}
.lizard-reports-stats li {
  display: inline-block;
}
.lizard-hourly-rate-input {
  text-align: center;
  width: 60px;
  max-width: 60px;
}
.header-msg {
  color: #666;
  font-weight: bold !important;
}
label {
  color: #666;
  line-height: 40px !important;
  font-weight: bold !important;
  font-size: 12px !important;
}
.txt {
  color: #666;
  font-weight: bold !important;
  font-size: 12px !important;
}
.lizard-header-cell {
  font-size: 8pt !important;
  font-weight: bold;
}
.lizard-row-cell {
  font-size: 8pt !important;
  font-weight: bold;
}
.lizard-row-cell:hover {
  background-color: #eee;
}

.allowence-table-header {
  font-size: 12px !important;
  font-weight: bold !important;
  color: #999 !important;
  text-align: center;
}
.allowence-data-row {
  font-size: 12px !important;
  font-weight: bold !important;
  color: #666 !important;
  text-align: center;
  min-height: 40px;
  line-height: 40px;
}
.allowence-data-row .allowence-input {
  border: 0;
  background: transparent;
  font-size: 12px !important;
  font-weight: bold !important;
  color: #666 !important;
  min-height: 40px;
  line-height: 40px;
  text-align: center;
  margin: 0;
  padding: 0;
}
.allowence-data-row:hover {
  background-color: rgba(102, 204, 255, 0.1);
}
</style>

<style scoped>
span.user-assignments {
  margin: 0;
  padding: 0;
}
span.user-assignments div h6 {
  font-size: 1.1em;
  vertical-align: middle;
}
span.user-assignments div h6 > .badge {
  vertical-align: middle;
  margin-top: 0.7em;
  text-shadow: none !important;
}
.stripe-bg {
  background: repeating-linear-gradient(
      25deg,
      transparent,
      transparent 10px,
      #93c 10px,
      #93c 20px
    ),
    linear-gradient(to bottom, #c39, #93c);
}
.wfa-stripe-bg {
  background: repeating-linear-gradient(
      25deg,
      transparent,
      transparent 10px,
      #400d01 10px,
      #400d01 20px
    ),
    linear-gradient(to bottom, #592202, #400d01);
}
ul.expense-currencies {
  list-style: none;
  padding: 0;
  margin: 0;
}
ul.expense-currencies li {
  display: inline-block;
  margin: 0 6px;
  font-family: 'Courier New', Courier, monospace;
}
ul.expense-currencies li:hover {
  cursor: default;
  color: #333;
}
</style>
